/**
 * outcomes: what became of a document from another system, in words, the same
 * on Settings, Your ERP and on /import/documents (lane erpin).
 *
 * @since 1.0.0 (erpin-2)
 */

export const OUTCOME_WORDS: Record<string, string> = {
    posted: 'posted', unchanged: 'already in Karr, unchanged', revised: 'changed before filing',
    amended: 'amended after filing', cancelled: 'cancelled', held: 'held for the office', refused: 'refused',
    pending: 'still being read',
    would_post: 'ready to post', would_revise: 'will change the one in Karr',
    would_amend: 'will amend the one already filed', would_cancel: 'will cancel the one in Karr',
    would_restore: 'will bring the cancelled one back',
};

export type OutcomeTone = 'good' | 'held' | 'refused';

export function outcomeTone(outcome: string | null | undefined): OutcomeTone {
    if (outcome === 'held') return 'held';
    if (outcome === 'refused' || !outcome) return 'refused';
    return 'good';
}

/** A dry run's outcome that a real run would act on. */
export const isReadyOutcome = (outcome: string | null | undefined): boolean =>
    !!outcome && outcome.startsWith('would_');

/** A fault as it comes back, from the validator or from the books. */
export interface ShownFault { code: string; field?: string | null; what: string; do: string; to?: string | null }

/**
 * The button that finishes a fault, on Karr's own pages. The ERP is never sent
 * a Karr route for what only the office can do, so the books' held faults get
 * theirs here: the period lock, or the credit note against the invoice the
 * arrival names. A fault with no screen that fixes it gets no button: one that
 * lands somewhere wrong is worse than the sentence alone.
 */
export function faultFix(f: ShownFault, karrId?: string | null): { to: string; label: string } | null {
    if (f.to) return { to: f.to, label: 'Fix this' };
    switch (f.code) {
        case 'PERIOD_LOCKED':
            return { to: '/period-locks', label: 'Open Close & Lock Periods' };
        case 'FILED_NEEDS_CREDIT_NOTE':
            return karrId ? { to: `/credit-notes?against=${encodeURIComponent(karrId)}&kind=credit`, label: 'Raise the credit note' } : null;
        case 'NOTE_AWAITS_APPROVAL':
            return { to: '/credit-notes', label: 'Open credit notes' };
        default:
            return null;
    }
}

/** What the month's count needs to know about one arrival. */
export interface ArrivalFacts {
    received_at: string;
    outcome: string | null;
    seller_gstin?: string | null; fy?: string | null; doc_type?: string | null; doc_no?: string | null;
    resolved_at?: string | null;
}

/**
 * The arrivals that still need a fix: for each document (seller GSTIN, year,
 * type, number) only its latest arrival counts, and that one needs a fix when
 * it was refused, or held with its office task still open. A document sent
 * again and taken, or a held one whose task is done, needs nothing more.
 * An arrival without a number cannot be matched to a later one, so it stands
 * on its own.
 */
export function stillNeedFix<T extends ArrivalFacts>(arrivals: T[]): T[] {
    const latest = new Map<string, T>();
    arrivals.forEach((a, i) => {
        const key = a.doc_no ? [a.seller_gstin ?? '', a.fy ?? '', a.doc_type ?? '', a.doc_no].join('|') : `#${i}`;
        const seen = latest.get(key);
        if (!seen || a.received_at > seen.received_at) latest.set(key, a);
    });
    return [...latest.values()]
        .filter((a) => a.outcome === 'refused' || (a.outcome === 'held' && !a.resolved_at))
        .sort((x, y) => (x.received_at < y.received_at ? 1 : -1));
}
